import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Tooltip } from '../../CommonComponents/OPL/Tooltip/Tooltip';
import { DEFAULT_ACCOUNT_ID, NOT_SELECTED, SELECTED } from '../TransactionsConstants';
import { Button as OPLButton } from '../../CommonComponents/OPL/Button/Button';
import { CategoriesMenu } from './CategoriesMenu/CategoriesMenu';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '../../CommonComponents/OPL/Menu/Menu';
import { ListItem } from '../../CommonComponents/OPL/List/ListItem';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { ScrollbarBox } from '../../CommonComponents/OPL/ScrollbarBox/ScrollbarBox';
import { Input as OPLInput } from '../../CommonComponents/OPL/Input/Input';
import { IconButton } from '../../CommonComponents/OPL/IconButton/IconButton';

const StyledTableControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1em;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1em;
  }
`;

const TableFilters = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  > * {
    margin-right: 1em;
  }
`;

const OverflowEllipsis = styled.div`
  overflow: hidden;
  max-width: 118px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const getCurrentPeriodLabel = (options, currentPeriodId) => {
  if (options && options.length > 0) {
    const currentPeriod = options.find(period => period.value === currentPeriodId);
    return currentPeriod.label;
  }
  return '';
};

const TableControls = ({
  onFilterOpen,
  onFilterClose,
  periodOptions,
  currentPeriodId,
  onAddTransactionClick,
  onPeriodChange,
  onQueryChange,
  query,
  expenseCategoryOptions,
  currentExpenseCategoryFilter,
  onExpenseCategoryFilterChange,
  currentAccount,
  accounts,
  onAccountChange,
}) => {
  const currentPeriodLabel = getCurrentPeriodLabel(periodOptions, currentPeriodId);

  const accountsItem = accounts ? accounts.filter(account => account.value !== '') : [];

  const onOpen = isOpen => {
    if (isOpen) {
      onFilterOpen();
    } else {
      onFilterClose();
    }
  };

  if (!expenseCategoryOptions) {
    return null;
  }

  return (
    <StyledTableControls>
      <TableFilters>
        <Menu onOpenChange={onOpen}>
          <MenuTrigger>
            <OPLButton
              css="min-width: 140px"
              size="large"
              variant={currentAccount.accountId === DEFAULT_ACCOUNT_ID ? 'secondary' : 'primary'}
            >
              {currentAccount.accountId !== DEFAULT_ACCOUNT_ID ? (
                <OverflowEllipsis>
                  {accounts.find(a => a.value === currentAccount.accountId).label}
                </OverflowEllipsis>
              ) : (
                'Accounts'
              )}
            </OPLButton>
          </MenuTrigger>
          <MenuContent>
            <ScrollbarBox css="max-height: 300px">
              {accountsItem.map(account => (
                <MenuItem key={account.value} onSelect={() => onAccountChange(account)}>
                  <ListItem
                    {...(account.value === currentAccount.accountId
                      ? SELECTED
                      : currentAccount.accountId !== ''
                      ? NOT_SELECTED
                      : {})}
                  >
                    {account.label}
                  </ListItem>
                </MenuItem>
              ))}
            </ScrollbarBox>

            {currentAccount.accountId !== '' && (
              <>
                <MenuSeparator />
                <MenuItem
                  onSelect={() => {
                    onAccountChange({ value: '', label: 'All Accounts' });
                  }}
                >
                  <Box display="flex" justifyContent="flex-end">
                    <Text color="primary.500">Clear</Text>
                  </Box>
                </MenuItem>
              </>
            )}
          </MenuContent>
        </Menu>

        <CategoriesMenu
          options={expenseCategoryOptions}
          selectedOption={currentExpenseCategoryFilter}
          setOption={onExpenseCategoryFilterChange}
          onOpen={onOpen}
          canClear
        >
          <OPLButton
            css="min-width: 140px"
            size="large"
            variant={currentExpenseCategoryFilter === '' ? 'secondary' : 'primary'}
            disabled={accountsItem.length === 0}
          >
            {currentExpenseCategoryFilter !== '' ? (
              <OverflowEllipsis>{currentExpenseCategoryFilter}</OverflowEllipsis>
            ) : (
              'Categories'
            )}
          </OPLButton>
        </CategoriesMenu>

        <Menu onOpenChange={onOpen}>
          <MenuTrigger>
            <OPLButton css="min-width: 140px" size="large">
              {currentPeriodLabel}
            </OPLButton>
          </MenuTrigger>
          <MenuContent>
            <Menu>
              {periodOptions.map(period => (
                <MenuItem key={period.value} onSelect={() => onPeriodChange(period)}>
                  <ListItem {...(period.value === currentPeriodId ? SELECTED : NOT_SELECTED)}>
                    {period.label}
                  </ListItem>
                </MenuItem>
              ))}
            </Menu>
          </MenuContent>
        </Menu>

        <Tooltip content="Add Transaction" placement="top">
          <td style={{ textAlign: 'center', justifyContent: 'center' }}>
            <IconButton
              onClick={onAddTransactionClick}
              icon="fa fa-plus-circle"
              shape="circle"
              size="small"
              variant="subtleInactive"
              aria-label="Add Transaction"
              style={{ alignItems: 'center', justifyContent: 'center' }}
            />
          </td>
        </Tooltip>
      </TableFilters>

      <OPLInput
        value={query}
        placeholder="Search..."
        onChange={({ target: { value } }) => onQueryChange(value)}
        leftIcon="fa fa-search"
        disabled={accountsItem.length === 0}
        css={{ flex: 1 }}
        aria-label="Search"
      />
    </StyledTableControls>
  );
};

TableControls.propTypes = {
  periodOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  currentPeriodId: PropTypes.string.isRequired,
  onAddTransactionClick: PropTypes.func.isRequired,
  onPeriodChange: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  expenseCategoryOptions: PropTypes.arrayOf({
    label: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  currentExpenseCategoryFilter: PropTypes.string.isRequired,
  onExpenseCategoryFilterChange: PropTypes.string.isRequired,
  currentAccount: PropTypes.shape({
    accountId: PropTypes.string.isRequired,
  }).isRequired,
  accounts: PropTypes.string.isRequired,
  onAccountChange: PropTypes.func.isRequired,
  onFilterOpen: PropTypes.func.isRequired,
  onFilterClose: PropTypes.func.isRequired,
};

export default TableControls;
